import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";

import SnippetDisplay from "./SnippetDisplay";

import { useSnippetDialog } from "@/components/Playground/Messages/SnippetDialog/snippet-store";
import { PromptTemplateConfiguration } from "@/types/evaluate";
import "./styles.css";

const Snippet = (props: NodeViewProps) => {
  const dialog = useSnippetDialog();
  const updateTimeoutRef = useRef<number | null>(null);
  const mountTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { node } = props;
  const [configuration, setConfiguration] = useState<Partial<PromptTemplateConfiguration>>({
    template: {
      name: node.attrs.name || "",
      version_number: node.attrs.version_number || undefined,
      label: node.attrs.label || undefined,
    },
  });

  useEffect(() => {
    if (!node.attrs.mounted) {
      // Use setTimeout to defer the state updates to the next tick
      mountTimeoutRef.current = setTimeout(() => {
        // eslint-disable-next-line react/destructuring-assignment
        props.updateAttributes({ mounted: true });
        dialog?.openDialog(configuration, setConfiguration);
        mountTimeoutRef.current = null;
      }, 0);
    }

    // Cleanup function
    return () => {
      if (mountTimeoutRef.current) {
        clearTimeout(mountTimeoutRef.current);
        mountTimeoutRef.current = null;
      }
    };
  }, [configuration, setConfiguration, dialog, node.attrs.mounted, props]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isNullish = (value: any) => value === null || value === undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const areEqual = (a: any, b: any) => (isNullish(a) && isNullish(b) ? true : a === b);

    const { name, version_number, label } = configuration?.template || {};
    const { attrs } = node;

    const hasChanges =
      !areEqual(attrs.name, name) ||
      !areEqual(attrs.version_number, version_number) ||
      !areEqual(attrs.label, label);

    if (hasChanges) {
      // Cancel any pending update
      if (updateTimeoutRef.current) {
        cancelAnimationFrame(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }

      // Schedule the update on the next animation frame to coalesce rapid successive
      // changes while keeping the ProseMirror document in sync before any save call
      // can read it (eliminates the 100 ms race window from the previous setTimeout).
      updateTimeoutRef.current = requestAnimationFrame(() => {
        // Check if component is still mounted before updating
        if (updateTimeoutRef.current) {
          // eslint-disable-next-line react/destructuring-assignment
          props.updateAttributes({ name, version_number, label });
          updateTimeoutRef.current = null;
        }
      });
    }

    // Cleanup function for this effect
    return () => {
      if (updateTimeoutRef.current) {
        cancelAnimationFrame(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration, props]);

  // eslint-disable-next-line react/destructuring-assignment
  if (!props.editor.isEditable) {
    return (
      <SnippetDisplay
        promptName={node.attrs.name}
        version={node.attrs.version_number}
        label={node.attrs.label}
        isReadOnly
      />
    );
  }

  return (
    <NodeViewWrapper className="inline-flex min-w-0 max-w-full">
      <SnippetDisplay
        promptName={node.attrs.name}
        onEdit={() => {
          dialog?.openDialog(
            {
              template: {
                name: node.attrs.name,
                version_number: node.attrs.version_number,
                label: node.attrs.label,
              },
            },
            value => {
              setConfiguration(value);
            },
          );
        }}
        onVersionLabelChange={(version, label) => {
          setConfiguration({
            template: {
              name: node.attrs.name,
              version_number: version,
              label: label,
            },
          });
        }}
        version={node.attrs.version_number}
        label={node.attrs.label}
        // eslint-disable-next-line react/destructuring-assignment
        isReadOnly={!props.editor.isEditable}
      />
    </NodeViewWrapper>
  );
};

export default Snippet;
